import {useEffect, useMemo, useRef, useState} from 'react'
import {
  Animated,
  Easing,
  StyleSheet,
  Text,
  TouchableOpacity,
  View,
} from 'react-native'
import {Feather} from '@expo/vector-icons'
import {Trans, useLingui} from '@lingui/react/macro'

import {
  createMicLevelSource,
  easeMeter,
  isVoicePresent,
  METER_BARS,
  pushMeterBar,
} from '#/lib/flashbookMicLevel'
import {readableInk, type StudioChrome} from '#/themes-studio/chrome'
import {useStudioChrome, useStudioStyles} from '#/themes-studio/useChrome'

type Props = {
  /** How long the take has been running, from the recorder itself. */
  elapsedMs: number
  /** The block's own accent, so the card belongs to the page it sits on. */
  accent: string
  /** Paused: the take is held, not ended. */
  paused: boolean
  /** The recorder's own live level, where the platform reports one. */
  readDbfs?: () => number | null | undefined
  onTogglePause: () => void
  onUse: () => void
  onDiscard: () => void
}

/** `0:07` — the same clock the field's player uses, so a take reads alike everywhere. */
function clock(ms: number): string {
  const total = Math.max(0, Math.floor(ms / 1000))
  const m = Math.floor(total / 60)
  const s = total % 60
  return `${m}:${String(s).padStart(2, '0')}`
}

/**
 * How long a take can run in silence before the card says so.
 *
 * Long enough that a creator drawing breath, or reading the sentence they are
 * about to say, is not interrupted by a warning; short enough that a muted
 * microphone is caught while they are still in front of it rather than after they
 * have listened to nothing.
 */
const SILENCE_ALARM_MS = 4000

/**
 * The recording card.
 *
 * A recorder that only shows a red dot and a number is a timer, and a timer
 * answers none of the questions a creator actually has while their take is
 * running: *is it hearing me*, *was that too quiet*, *can I stop and start again
 * without losing the beginning*. So this is built around the level, in the way
 * the machines that record for a living are:
 *
 *  - **the meter is the microphone's**, taken from the device's own metering or
 *    from the browser's samples — never from the clock. A decoration that moved
 *    on its own would be a lie about the one thing this screen exists to show,
 *    and where the platform genuinely cannot measure (see `flashbookMicLevel`),
 *    the meter is *absent* rather than pretending;
 *  - **bars, not a bar**: the same shape a waveform editor uses, so what is being
 *    captured is legible as speech — a sentence has a shape, and the creator can
 *    see whether theirs landed;
 *  - **the badge breathes with the voice**, which is the difference between a
 *    screen that is *recording* and a screen that is *listening*;
 *  - **silence is reported**, because a muted microphone is the single most
 *    common way to waste a minute of someone's time;
 *  - **pause and discard are separate actions**, since "let me say that again"
 *    and "not this take" are different intentions and only one of them throws
 *    away a recording the creator is still happy with.
 */
export function FlashBookRecordingCard({
  elapsedMs,
  accent,
  paused,
  readDbfs,
  onTogglePause,
  onUse,
  onDiscard,
}: Props) {
  const chrome = useStudioChrome()
  const styles = useStyles()
  const {t: l} = useLingui()
  const accentInk = readableInk(accent).ink

  const source = useMemo(() => createMicLevelSource(readDbfs), [readDbfs])
  const [bars, setBars] = useState<number[]>([])
  const [level, setLevel] = useState(0)
  const [heardVoice, setHeardVoice] = useState(false)

  // The badge's breathing: driven by the level, so it is the creator's voice that
  // makes the screen move.
  const glow = useRef(new Animated.Value(0)).current
  const idle = useRef(new Animated.Value(1)).current
  const levelRef = useRef(0)

  useEffect(() => {
    if (!source.supported) return
    let smoothed = 0
    const stop = source.start(next => {
      /*
       * Attack fast, release slow — a physical meter's movement. Done here rather
       * than only in the drawing, because the *shape of the history* is what makes
       * it read as speech: reacting instantly in both directions turns a sentence
       * into a static bar.
       */
      smoothed = easeMeter(smoothed, next)
      levelRef.current = smoothed
      setLevel(smoothed)
      setBars(previous => pushMeterBar(previous, smoothed))
      if (isVoicePresent(smoothed)) setHeardVoice(true)
    })
    return stop
  }, [source])

  useEffect(() => {
    const animation = Animated.timing(glow, {
      toValue: level,
      duration: 90,
      easing: Easing.out(Easing.quad),
      useNativeDriver: true,
    })
    animation.start()
    return () => animation.stop()
  }, [glow, level])

  // The dot's own pulse: the visual that says a take is running, and that stops the
  // moment it is paused. Never tied to the level, so a quiet room still reads as
  // "recording".
  useEffect(() => {
    if (paused) {
      idle.setValue(1)
      return
    }
    const loop = Animated.loop(
      Animated.sequence([
        Animated.timing(idle, {
          toValue: 0.25,
          duration: 620,
          easing: Easing.inOut(Easing.quad),
          useNativeDriver: true,
        }),
        Animated.timing(idle, {
          toValue: 1,
          duration: 620,
          easing: Easing.inOut(Easing.quad),
          useNativeDriver: true,
        }),
      ]),
    )
    loop.start()
    return () => loop.stop()
  }, [idle, paused])

  const silent = !paused && elapsedMs >= SILENCE_ALARM_MS && !heardVoice
  const speaking = !paused && isVoicePresent(level)

  return (
    <View
      testID="flashbook-recording-card"
      style={[styles.card, {borderColor: accent + '66'}]}>
      <View style={styles.head}>
        <Animated.View
          testID="flashbook-recording-dot"
          style={[
            styles.dot,
            {backgroundColor: chrome.danger, opacity: paused ? 1 : idle},
          ]}
        />
        <Text style={[styles.state, {color: accent}]}>
          {paused ? l`Paused` : l`Recording`}
        </Text>
        <Text testID="flashbook-recording-clock" style={styles.clock}>
          {clock(elapsedMs)}
        </Text>
      </View>

      {/*
        The meter. Every bar is a moment of the last second and a half of the
        take, so the row reads left-to-right as *what was just said*.
      */}
      <View style={styles.meter} testID="flashbook-recording-meter">
        {Array.from({length: METER_BARS}, (_, index) => {
          const level = bars[index] ?? 0
          // Bars older than the take's own history are a resting line rather than
          // an empty gap, so the meter looks like silence instead of like a meter
          // that has not started.
          const height = 3 + Math.round(level * 33)
          const recent = index >= METER_BARS - 6
          return (
            <View
              key={index}
              style={[
                styles.bar,
                {
                  height,
                  backgroundColor: recent ? accent : accent + '77',
                  // Signed by the newest bars, which is how a waveform bar reads
                  // as a voice rather than as a chart.
                  opacity: paused ? 0.5 : 1,
                },
              ]}
            />
          )
        })}
      </View>

      <View style={styles.statusRow}>
        {!source.supported ? (
          <Text
            style={
              styles.status
            }>{l`Recording — this device cannot show a level.`}</Text>
        ) : silent ? (
          <>
            <Feather name="mic-off" size={12} color={chrome.warning} />
            <Text style={[styles.status, {color: chrome.warning}]}>
              {l`We cannot hear anything yet — check your microphone.`}
            </Text>
          </>
        ) : (
          <>
            <Animated.View
              style={[
                styles.ping,
                {
                  backgroundColor: speaking ? accent : chrome.textFaint,
                  transform: [
                    {
                      scale: glow.interpolate({
                        inputRange: [0, 1],
                        outputRange: [0.7, 1.8],
                      }),
                    },
                  ],
                },
              ]}
            />
            <Text
              testID="flashbook-recording-state"
              style={[styles.status, speaking && {color: accent}]}>
              {paused
                ? l`Your take is held where it is.`
                : speaking
                  ? l`We are hearing you.`
                  : l`Start speaking…`}
            </Text>
          </>
        )}
      </View>

      <View style={styles.actions}>
        <TouchableOpacity
          accessibilityRole="button"
          accessibilityLabel={paused ? l`Resume recording` : l`Pause recording`}
          accessibilityHint={
            paused
              ? l`Carries on from where the take was paused`
              : l`Holds the take without ending it`
          }
          onPress={onTogglePause}
          testID="flashbook-recording-pause"
          style={[styles.ghost, {borderColor: chrome.borderStrong}]}>
          <Feather
            name={paused ? 'mic' : 'pause'}
            size={13}
            color={chrome.textMuted}
          />
          <Text style={[styles.ghostTxt, {color: chrome.textMuted}]}>
            {paused ? l`Resume` : l`Pause`}
          </Text>
        </TouchableOpacity>

        <View style={styles.spacer} />

        <TouchableOpacity
          accessibilityRole="button"
          accessibilityLabel={l`Discard recording`}
          accessibilityHint={l`Throws this recording away`}
          onPress={onDiscard}
          testID="flashbook-recording-discard"
          style={styles.discard}>
          <Feather name="trash-2" size={13} color={chrome.danger} />
        </TouchableOpacity>

        <TouchableOpacity
          accessibilityRole="button"
          accessibilityLabel={l`Use recording`}
          accessibilityHint={l`Attaches this recording to the block`}
          onPress={onUse}
          testID="flashbook-recording-use"
          style={[styles.solid, {backgroundColor: accent}]}>
          <Feather name="check" size={14} color={accentInk} />
          <Text style={[styles.solidTxt, {color: accentInk}]}>
            <Trans>Use take</Trans>
          </Text>
        </TouchableOpacity>
      </View>
    </View>
  )
}

const makeStyles = (chrome: StudioChrome) =>
  StyleSheet.create({
    card: {
      borderWidth: 1,
      borderRadius: 16,
      paddingHorizontal: 12,
      paddingVertical: 11,
      gap: 9,
      backgroundColor: chrome.dangerTint,
    },
    head: {flexDirection: 'row', alignItems: 'center', gap: 8},
    dot: {width: 9, height: 9, borderRadius: 5},
    state: {flex: 1, fontSize: 12.5, fontWeight: '800', letterSpacing: 0.2},
    clock: {
      fontSize: 17,
      fontWeight: '800',
      letterSpacing: -0.3,
      color: chrome.text,
      fontVariant: ['tabular-nums'],
    },
    /*
     * The meter's own ground: a quiet inset so the bars have something to rise
     * from, and tall enough (36) for a full-scale level without the card jumping
     * as it moves.
     */
    meter: {
      height: 38,
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: 2,
    },
    bar: {flex: 1, borderRadius: 2, minWidth: 2},
    statusRow: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 6,
      minHeight: 16,
    },
    ping: {width: 6, height: 6, borderRadius: 3},
    status: {
      flex: 1,
      fontSize: 11.5,
      fontWeight: '600',
      color: chrome.textMuted,
    },
    actions: {flexDirection: 'row', alignItems: 'center', gap: 8},
    spacer: {flex: 1},
    ghost: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 6,
      borderWidth: 1,
      borderRadius: 18,
      paddingHorizontal: 12,
      paddingVertical: 7,
    },
    ghostTxt: {fontSize: 12.5, fontWeight: '700'},
    discard: {
      width: 34,
      height: 34,
      borderRadius: 17,
      alignItems: 'center',
      justifyContent: 'center',
      borderWidth: 1,
      borderColor: chrome.dangerBorder,
    },
    solid: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 6,
      borderRadius: 18,
      paddingHorizontal: 14,
      paddingVertical: 8,
    },
    solidTxt: {fontSize: 13, fontWeight: '800'},
  })

const useStyles = () => useStudioStyles(makeStyles)
